import * as THREE from 'three';
import type { StyleConfig } from './StyleConfig';

const tmpA = new THREE.Color();
const tmpB = new THREE.Color();
const tmpC = new THREE.Color();

export function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

export function smoothstep(edge0: number, edge1: number, value: number): number {
  const t = clamp01((value - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}

export function sunColorByElevation(elevationDeg: number, warmthBias = 0): string {
  const stops: Array<[number, string]> = [
    [-20, '#101525'],
    [-10, '#2a3658'],
    [0, '#d76886'],
    [5, '#ff8844'],
    [20, '#ffd38a'],
    [75, '#fff5e0'],
    [90, '#ffffff'],
  ];
  const warmed = THREE.MathUtils.clamp(elevationDeg + warmthBias * 10, -20, 90);
  for (let i = 0; i < stops.length - 1; i++) {
    const [fromElev, fromHex] = stops[i];
    const [toElev, toHex] = stops[i + 1];
    if (warmed <= toElev) {
      const t = smoothstep(fromElev, toElev, warmed);
      return lerpHex(fromHex, toHex, t);
    }
  }
  return stops[stops.length - 1][1];
}

export function sunIntensityByElevation(elevationDeg: number): number {
  return Math.pow(Math.max(0, Math.sin(THREE.MathUtils.degToRad(elevationDeg))), 1.5) * 4;
}

export function fillIntensityFromSun(sunIntensity: number, secondaryScale = 1): number {
  return sunIntensity * 0.35 * THREE.MathUtils.clamp(secondaryScale, 0, 2);
}

export function fogDensityByElevation(elevationDeg: number, hazeBoost = 1, turbidity = 2): number {
  const horizonGrazing = 1 - smoothstep(8, 55, elevationDeg);
  const nightLift = 1 - smoothstep(-16, 6, elevationDeg);
  const turbidityScale = THREE.MathUtils.clamp(turbidity / 4, 0.25, 2.5);
  const hazeScale = THREE.MathUtils.clamp(hazeBoost, 0, 2);
  return (0.00016 + horizonGrazing * 0.00074 + nightLift * 0.00024) * hazeScale * turbidityScale;
}

export function hemiIntensityByElevation(elevationDeg: number): number {
  return 0.6 + 0.4 * smoothstep(-10, 30, elevationDeg);
}

export function environmentIntensityByElevation(elevationDeg: number): number {
  return 0.18 + 0.18 * smoothstep(-10, 60, elevationDeg);
}

export function horizonHueShiftByAtmosphere(elevationDeg: number, warmthBias = 0, turbidity = 2): number {
  const lowSun = 1 - smoothstep(6, 45, elevationDeg);
  const hazeWarmth = smoothstep(2, 8, turbidity) * 0.28;
  return THREE.MathUtils.clamp(warmthBias * 0.35 + lowSun * hazeWarmth, -1, 1);
}

export function aerialPerspectiveTint(
  sky: StyleConfig['sky'],
  sunColorHex: string,
  elevationDeg: number,
  turbidity = 2,
): string {
  const lowSun = 1 - smoothstep(0, 42, elevationDeg);
  const mie = THREE.MathUtils.clamp(0.18 + lowSun * 0.42 + (turbidity - 2) * 0.035, 0.08, 0.72);
  return lerpHex(sky.horizonHex, sunColorHex, mie);
}

export function dimHex(hex: string, amount: number): string {
  tmpA.set(hex);
  tmpA.multiplyScalar(amount);
  return colorToHex(tmpA);
}

export function multiplyHex(a: string, b: string): string {
  tmpA.set(a);
  tmpB.set(b);
  tmpA.r *= tmpB.r;
  tmpA.g *= tmpB.g;
  tmpA.b *= tmpB.b;
  return colorToHex(tmpA);
}

export function lerpHex(fromHex: string, toHex: string, t: number): string {
  tmpA.set(fromHex);
  tmpB.set(toHex);
  tmpC.copy(tmpA).lerp(tmpB, clamp01(t));
  return colorToHex(tmpC);
}

function colorToHex(color: THREE.Color): string {
  return `#${color.getHexString()}`;
}
